import { Fluent } from "@serenity-is/corelib";
import { bindThis, signal } from "@serenity-is/domwise";
import { FooterRow, Header, HeaderRow, TopPanel, Viewport, type Column, type GridEvent, type GridLayoutRefs, type GridOptions, type ISleekGrid, type LayoutEngine, type LayoutHost } from "@serenity-is/sleekgrid";
import { HeaderMenuAction, HeaderMenuPlugin, type HeaderMenuActionEvent, type HeaderMenuShowEvent } from "../GridMixins/HeaderMenuPlugin";
import { HeaderMenuTexts } from "../../ServerTypes/Texts";

declare module "@serenity-is/sleekgrid" {
    interface Column<TItem = any> {
        /** If false, pinning actions in the header menu will be hidden for this column */
        pinnable?: boolean;
    }
}

declare module "../GridMixins/HeaderMenuPlugin" {
    interface HeaderMenuOptions {
        /** If false, pinning actions will be hidden for this column */
        pinnable?: boolean;
    }
}

export class EnhancedLayout implements LayoutEngine {
    declare private host: LayoutHost;
    declare private headerMenuGrid: ISleekGrid;
    declare private refs: GridLayoutRefs;
    private hidePinning = signal(false);
    private pinLeftText = signal("");
    private pinRightText = signal("");

    public init(host: LayoutHost) {
        this.host = host;
        this.refs = host.refs;
        const signals = host.getSignals();
        const common = { refs: this.refs, signals };

        host.onAfterInit.subscribe(bindThis(this).handleGridAfterInit);
        host.getContainerNode().append(<>
            <Header band="start" {...common} />
            <Header band="main" {...common} />
            <Header band="end" {...common} />
            <TopPanel {...common} />
            <HeaderRow band="start" {...common} />
            <HeaderRow band="main" {...common} />
            <HeaderRow band="end" {...common} />
            <Viewport band="start" pane="top" {...common} />
            <Viewport band="main" pane="top" {...common} />
            <Viewport band="end" pane="top" {...common} />
            <Viewport band="start" pane="body" {...common} />
            <Viewport band="main" pane="body" {...common} />
            <Viewport band="end" pane="body" {...common} />
            <Viewport band="start" pane="bottom" {...common} />
            <Viewport band="main" pane="bottom" {...common} />
            <Viewport band="end" pane="bottom" {...common} />
            <FooterRow band="start" {...common} />
            <FooterRow band="main" {...common} />
            <FooterRow band="end" {...common} />
        </>);

        this.adjustFrozenRowsOption();
    }

    public destroy(): void {
        this.host?.onAfterInit?.unsubscribe(this.handleGridAfterInit);
        if (this.headerMenuGrid) {
            let headerMenuPlugin = HeaderMenuPlugin.tryGetInstance(this.headerMenuGrid);
            if (headerMenuPlugin) {
                Fluent.remove(headerMenuPlugin.menu?.querySelector(`[data-action="pin-left"]`));
                Fluent.remove(headerMenuPlugin.menu?.querySelector(`[data-action="pin-right"]`));
                headerMenuPlugin.onShow.unsubscribe(this.handleHeaderMenuShow);
                headerMenuPlugin.onAction.unsubscribe(this.handleHeaderMenuAction);
            }
        }
        this.host = this.refs = this.headerMenuGrid = null;
    }

    private handleGridAfterInit({ grid }: Pick<GridEvent, "grid">) {
        const register = () => {
            let headerMenuPlugin = HeaderMenuPlugin.tryGetInstance(grid);
            if (!headerMenuPlugin)
                return;
            headerMenuPlugin.addSplitAction({
                text: HeaderMenuTexts.Pin,
                hidden: this.hidePinning,
                leftContent:
                    <HeaderMenuAction action="pin-left" hidden={this.hidePinning}>
                        <PinImage /> {this.pinLeftText}
                    </HeaderMenuAction>,
                rightContent:
                    <HeaderMenuAction action="pin-right" hidden={this.hidePinning}>
                        <PinImage right /> {this.pinRightText}
                    </HeaderMenuAction>
            });
            this.headerMenuGrid = grid;
            headerMenuPlugin.onShow.subscribe(bindThis(this).handleHeaderMenuShow);
            headerMenuPlugin.onAction.subscribe(bindThis(this).handleHeaderMenuAction);
        };

        // if the header menu plugin is not yet initialized, wait a tick
        !HeaderMenuPlugin.tryGetInstance(grid) ? queueMicrotask(register) : register();
    }

    private handleHeaderMenuShow({ column, grid, args }: HeaderMenuShowEvent) {
        this.hidePinning.value = args.plugin.options?.pinnable == false || column.pinnable === false;
        const pinnedStart = column.frozen && column.frozen !== "end";
        const pinnedEnd = column.frozen === "end";
        const rtl = grid.getOptions()?.rtl;
        this.pinLeftText.value = (rtl ? pinnedEnd : pinnedStart) ? (HeaderMenuTexts.PinRemove + " ✗") : HeaderMenuTexts.PinLeft;
        this.pinRightText.value = (rtl ? pinnedStart : pinnedEnd) ? (HeaderMenuTexts.PinRemove + " ✗") : HeaderMenuTexts.PinRight;
    }

    private handleHeaderMenuAction(e: HeaderMenuActionEvent) {
        const { args, column, grid } = e;
        const rtl = grid.getOptions()?.rtl;
        if (args.action === "pin-left" ||
            args.action === "pin-right"
        ) {
            e.preventDefault();
            let pinLeft = args.action === "pin-left";
            if (rtl)
                pinLeft = !pinLeft;
            if (pinLeft && column.frozen && column.frozen !== "end" ||
                !pinLeft && column.frozen === "end") {
                column.frozen = false;
            }
            else {
                column.frozen = pinLeft ? "start" : "end";
            }
            grid.invalidateColumns();
            grid.onColumnsReordered.notify({ grid }, null, grid);
        }
    }

    public reorderViewColumns(viewCols: Column[], refs: GridLayoutRefs): Column[] {
        const pinnedStartCols = viewCols.filter(x => x.frozen && x.frozen !== 'end');
        const pinnedEndCols = viewCols.filter(x => x.frozen === 'end');
        refs.config.pinnedStartCols = pinnedStartCols.length;
        refs.config.pinnedEndCols = pinnedEndCols.length;
        if (pinnedStartCols.length > 0 || pinnedEndCols.length > 0)
            return pinnedStartCols.concat(viewCols.filter(x => !x.frozen)).concat(pinnedEndCols);
        return null;
    }

    public afterSetOptions(arg: GridOptions) {
        if (arg.frozenRows != null || arg.frozenBottom != null)
            this.adjustFrozenRowsOption();
        if (arg.frozenColumns != null && arg.columns == null) {
            const columns = this.reorderViewColumns(this.host.getAllColumns(), this.refs);
            if (columns != null)
                arg.columns = columns;
        }
    }

    public adjustFrozenRowsOption(): void {
        const { frozenRows, frozenBottom } = this.host.getOptions();
        let frozenTopRows = frozenBottom === true ? 0 : (frozenRows ?? 0);
        let frozenBottomRows = frozenBottom === true ? (frozenRows ?? 0) : (typeof frozenBottom === 'number' ? frozenBottom : 0);
        this.refs.config.frozenTopRows = frozenTopRows;
        this.refs.config.frozenBottomRows = frozenBottomRows;
    }

    public readonly layoutName = "EnhancedLayout";
}

/** From Bootstrap icons (https://icons.getbootstrap.com/) */
const PinImage = ({ right }: { right?: boolean }) => {
    return <svg xmlns="http://www.w3.org/2000/svg" style={{ transform: right ? "rotate(270deg)" : null, width: "1rem", height: "1rem" }} fill="currentColor" class="bi bi-pin-angle" viewBox="0 0 16 16">
        <path d="M9.828.722a.5.5 0 0 1 .354.146l4.95 4.95a.5.5 0 0 1 0 .707c-.48.48-1.072.588-1.503.588-.177 0-.335-.018-.46-.039l-3.134 3.134a6 6 0 0 1 .16 1.013c.046.702-.032 1.687-.72 2.375a.5.5 0 0 1-.707 0l-2.829-2.828-3.182 3.182c-.195.195-1.219.902-1.414.707s.512-1.22.707-1.414l3.182-3.182-2.828-2.829a.5.5 0 0 1 0-.707c.688-.688 1.673-.767 2.375-.72a6 6 0 0 1 1.013.16l3.134-3.133a3 3 0 0 1-.04-.461c0-.43.108-1.022.589-1.503a.5.5 0 0 1 .353-.146m.122 2.112v-.002zm0-.002v.002a.5.5 0 0 1-.122.51L6.293 6.878a.5.5 0 0 1-.511.12H5.78l-.014-.004a5 5 0 0 0-.288-.076 5 5 0 0 0-.765-.116c-.422-.028-.836.008-1.175.15l5.51 5.509c.141-.34.177-.753.149-1.175a5 5 0 0 0-.192-1.054l-.004-.013v-.001a.5.5 0 0 1 .12-.512l3.536-3.535a.5.5 0 0 1 .532-.115l.096.022c.087.017.208.034.344.034q.172.002.343-.04L9.927 2.028q-.042.172-.04.343a1.8 1.8 0 0 0 .062.46z" />
    </svg>
}